import React, { useEffect, useLayoutEffect, useState } from 'react';
import axios from 'axios';
import { FiX } from 'react-icons/fi';
import SimonCustomerModal from './SimonCustomerModal';

const SOURCE_CODES_BY_SUPPLIER = {
    Bean: ['BeanSup1', 'BeanSup2', 'BeanSup3'],
    Doraemon: ['DoraemonSupB', 'DoraemonSupCD']
};
const SUPPLIER_DELIVERY_FEES_BY_SOURCE = {
    BeanSup1: 20,
    BeanSup2: 20,
    BeanSup3: 20,
    DoraemonSupB: 20,
    DoraemonSupCD: 15
};

const emptyProductLine = { product: '', quantity: '1', price: '', cost: '', remarks: '' };
const emptyBundleLine = { bundleProduct: '', quantity: '1', price: '', cost: '', remarks: '' };
const emptyCreditDebitLine = { type: 'credit', amount: '', remarks: '' };
const emptyTelegram = { tgNumber: '', tgID: '', tgName: '' };
const inputClassName = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';
const labeledInputWrapperClassName = 'flex items-center border border-gray-300 rounded-md bg-white min-w-0 w-full';
const labeledTextAreaWrapperClassName = 'flex items-start border border-gray-300 rounded-md bg-white min-w-0 w-full';
const labeledInputLabelClassName = 'px-3 py-2 text-sm text-gray-600 font-semibold whitespace-nowrap shrink-0';
const labeledInputFieldClassName = 'flex-1 py-2 px-2 text-blue-700 font-bold bg-transparent border-none outline-none rounded-md w-full max-w-full';
const labeledTextAreaFieldClassName = 'flex-1 py-2 px-2 text-blue-700 font-bold bg-transparent border-none outline-none rounded-md resize-y min-h-[44px] w-full max-w-full';
const normalizeSearchText = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const getProductLabel = (product) => `${product.category} - ${product.productName}`;
const getBundleLabel = (bundle) => bundle?.bundleName || '';
const getCustomerNames = (customer) => {
    const names = Array.isArray(customer?.names) ? customer.names.filter(Boolean) : [];
    if (names.length) return names;
    if (customer?.name) return [String(customer.name)];
    return [];
};
const getLatestBundlePrice = (bundle) => {
    if (!bundle) return 0;
    const history = Array.isArray(bundle.bundlePriceHistory) ? bundle.bundlePriceHistory : [];
    const latest = [...history]
        .filter((row) => row?.effectiveFrom && typeof row?.price === 'number')
        .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0];
    if (latest && typeof latest.price === 'number') return latest.price;
    return Number(bundle.bundlePrice || 0);
};
const getResolvedProductUnitCost = (productDoc, supplier, supplierSource, orderDate) => {
    const source = (productDoc?.supplierSources || []).find(
        (item) => item.supplier === supplier && item.sourceCode === supplierSource
    );
    if (!source) return 0;
    const applicableRows = (source.costHistory || [])
        .filter((row) => row?.effectiveFrom && row?.cost != null && new Date(row.effectiveFrom) <= new Date(orderDate))
        .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom));
    return applicableRows.length ? Number(applicableRows[0].cost || 0) : 0;
};

const getResolvedProductUnitPrice = (productDoc, orderDateValue) => {
    if (!productDoc) return null;
    if (!orderDateValue) {
        const raw = productDoc.unitPrice;
        return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
    }
    const applicableRows = (productDoc.unitPriceHistory || [])
        .filter(
            (row) =>
                row?.effectiveFrom &&
                row?.unitPrice != null &&
                new Date(row.effectiveFrom) <= new Date(orderDateValue)
        )
        .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom));
    if (applicableRows.length > 0) {
        const n = Number(applicableRows[0].unitPrice);
        return Number.isFinite(n) ? n : null;
    }
    const raw = productDoc.unitPrice;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
};
const getResolvedBundleUnitCost = (bundleDoc, productsData, supplier, supplierSource, orderDate) => {
    return (bundleDoc?.products || []).reduce((sum, bundleItem) => {
        const productId = bundleItem?.product?._id || bundleItem?.product;
        if (!productId) return sum;
        const normalizedProductId = String(productId);
        const productDoc = productsData.find((item) => String(item._id) === normalizedProductId);
        const unitCost = getResolvedProductUnitCost(productDoc, supplier, supplierSource, orderDate);
        return sum + (unitCost * Number(bundleItem?.quantity || 0));
    }, 0);
};
const getSupplierDeliveryFeeBySource = (supplierSource) => {
    if (!supplierSource) return '';
    const fee = SUPPLIER_DELIVERY_FEES_BY_SOURCE[supplierSource];
    return fee != null ? String(fee) : '';
};

    const SOURCE_COST_COLUMNS = [
        'DoraemonSupB',
        'DoraemonSupCD',
        'BeanSup1',
        'BeanSup2',
        'BeanSup3'
    ];

    const formatDateYMD = (dateValue) => {
        if (!dateValue) return 'N/A';
        const d = new Date(dateValue);
        if (!Number.isFinite(d.getTime())) return 'N/A';
        return d.toISOString().slice(0, 10);
    };

    const formatMoneyOrNA = (value) => {
        if (value == null || !Number.isFinite(Number(value))) return 'N/A';
        return `$${Number(value).toFixed(2)}`;
    };

    const getLatestCostRowForSource = (productDoc, sourceCode, orderDateValue) => {
        const normalizedSourceCode = String(sourceCode || '').trim();
        const source = (productDoc?.supplierSources || []).find((s) => String(s?.sourceCode || '').trim() === normalizedSourceCode);
        const costHistory = source?.costHistory || [];
        if (!orderDateValue) return null;

        // Treat "as of order date" as end-of-day for date-only inputs, in UTC,
        // so comparisons against Mongo Date values (stored/serialized in UTC) are consistent.
        const od = (() => {
            if (typeof orderDateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(orderDateValue)) {
                const [y, m, d] = orderDateValue.split('-').map((x) => parseInt(x, 10));
                return new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999));
            }
            return new Date(orderDateValue);
        })();
        if (!Number.isFinite(od.getTime())) return null;

        // Find latest row <= order date.
        let latest = null;
        for (const row of costHistory) {
            if (!row?.effectiveFrom || row.cost == null) continue;
            const ed = new Date(row.effectiveFrom);
            if (!Number.isFinite(ed.getTime())) continue;
            if (ed <= od) {
                if (!latest || ed > latest.effectiveFromDate) {
                    latest = { effectiveFromDate: ed, cost: row.cost };
                }
            }
        }
        return latest;
    };

    const getProductLatestPricingSnapshot = (productDoc, orderDateValue) => {
        const latestBySource = {};
        let latestEffectiveDate = null;

        for (const sourceCode of SOURCE_COST_COLUMNS) {
            const latestRow = getLatestCostRowForSource(productDoc, sourceCode, orderDateValue);
            latestBySource[sourceCode] = latestRow || null;
            if (latestRow?.effectiveFromDate) {
                if (!latestEffectiveDate || latestRow.effectiveFromDate > latestEffectiveDate) {
                    latestEffectiveDate = latestRow.effectiveFromDate;
                }
            }
        }

        const latestEffectiveDateStr = latestEffectiveDate ? formatDateYMD(latestEffectiveDate) : 'N/A';
        const unitPriceValue = getResolvedProductUnitPrice(productDoc, orderDateValue);

        return {
            latestEffectiveDateStr,
            unitPriceValue,
            costsBySourceCode: latestBySource
        };
    };

const SimonOrderModal = ({ mode = 'create', order = null, customers = [], products = [], bundles = [], onSuccess, onCancel }) => {
    const [localCustomers, setLocalCustomers] = useState(customers || []);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearchQuery, setCustomerSearchQuery] = useState('');
    const [activeCustomerSearch, setActiveCustomerSearch] = useState(false);
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
    const [customerContactOptions, setCustomerContactOptions] = useState({
        names: [],
        addresses: [],
        phoneNumbers: [],
        whatsappNumbers: [],
        telegrams: []
    });
    const [productSearchQueries, setProductSearchQueries] = useState(['']);
    const [activeProductSearchIndex, setActiveProductSearchIndex] = useState(-1);
    const [bundleSearchQueries, setBundleSearchQueries] = useState(['']);
    const [activeBundleSearchIndex, setActiveBundleSearchIndex] = useState(-1);
    const [formData, setFormData] = useState({
        customerID: '',
        name: '',
        address: '',
        phoneNumber: '',
        whatsappNumber: '',
        telegram: { ...emptyTelegram },
        orderDate: new Date().toISOString().split('T')[0],
        deliveryDate: '',
        orderStatus: 'delivered',
        supplier: 'Doraemon',
        supplierSource: 'DoraemonSupB',
        products: [{ ...emptyProductLine }],
        bundles: [],
        orderDeliveryFees: '0',
        supplierDeliveryFees: '0',
        remarks: '',
        creditDebits: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState(false);
    const [showEditCustomerModal, setShowEditCustomerModal] = useState(false);

    useEffect(() => {
        setLocalCustomers(customers || []);
    }, [customers]);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const query = String(customerSearchQuery || '').trim();
            if (query.length < 3) {
                setCustomerSearchResults([]);
                setCustomerSearchLoading(false);
                return;
            }
            setCustomerSearchLoading(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/simoncustomers?page=1&limit=500&search=${encodeURIComponent(query)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (!cancelled) {
                    setCustomerSearchResults(response.data?.success ? (response.data?.data || []) : []);
                }
            } catch {
                if (!cancelled) setCustomerSearchResults([]);
            } finally {
                if (!cancelled) setCustomerSearchLoading(false);
            }
        };

        const timer = setTimeout(run, 250);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [customerSearchQuery]);

    // Customer search + selection must not depend on products/bundles churn (those array refs change often).
    useLayoutEffect(() => {
        if (mode !== 'edit' || !order) return;
        const cid = String(order.customerID ?? '').trim();
        const oname = String(order.name ?? '').trim();
        const list = customers || [];
        const matchedCustomer = list.find(
            (customer) => String(customer.customerID ?? '').trim() === cid
        );
        if (matchedCustomer) {
            setSelectedCustomer(matchedCustomer);
            setCustomerSearchQuery(
                `${String(matchedCustomer.customerID).trim()} - ${getCustomerNames(matchedCustomer).join(', ')}`
            );
            setCustomerContactOptions({
                names: matchedCustomer.names || [],
                addresses: matchedCustomer.addresses || [],
                phoneNumbers: matchedCustomer.phoneNumbers || [],
                whatsappNumbers: matchedCustomer.whatsappNumbers || [],
                telegrams: matchedCustomer.telegrams || []
            });
        } else {
            setSelectedCustomer(null);
            const fallbackLabel = [cid, oname].filter(Boolean).join(' - ');
            setCustomerSearchQuery(fallbackLabel || cid || '');
            setCustomerContactOptions({
                names: [],
                addresses: [],
                phoneNumbers: [],
                whatsappNumbers: [],
                telegrams: []
            });
        }
    }, [mode, order, customers]);

    // If customer exists in DB but is not in the preloaded list, hydrate selection via API.
    useEffect(() => {
        if (mode !== 'edit' || !order) return;
        const cid = String(order.customerID ?? '').trim();
        if (!cid) return;
        const list = customers || [];
        if (list.some((c) => String(c.customerID ?? '').trim() === cid)) return;

        let cancelled = false;
        (async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/simoncustomers?page=1&limit=50&search=${encodeURIComponent(cid)}`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                const rows = res.data?.success ? (res.data?.data || []) : [];
                const found = rows.find((c) => String(c.customerID ?? '').trim() === cid);
                if (cancelled || !found) return;
                setSelectedCustomer(found);
                setCustomerSearchQuery(`${String(found.customerID).trim()} - ${getCustomerNames(found).join(', ')}`);
                setCustomerContactOptions({
                    names: found.names || [],
                    addresses: found.addresses || [],
                    phoneNumbers: found.phoneNumbers || [],
                    whatsappNumbers: found.whatsappNumbers || [],
                    telegrams: found.telegrams || []
                });
            } catch {
                /* keep snapshot fallback from layout effect */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [mode, order, customers]);

    useEffect(() => {
        if (mode === 'edit' && order) {
            const normalizedProducts = order.products?.length
                ? order.products.map((item) => ({
                    product: item.product?._id || item.product || '',
                    quantity: item.quantity != null ? String(item.quantity) : '1',
                    price: item.price != null ? String(item.price) : '',
                    cost: item.cost != null ? String(item.cost) : '',
                    remarks: item.remarks || ''
                }))
                : [{ ...emptyProductLine }];

            const normalizedBundles = order.bundles?.length
                ? order.bundles.map((item) => ({
                    bundleProduct: item.bundleProduct?._id || item.bundleProduct || '',
                    quantity: item.quantity != null ? String(item.quantity) : '1',
                    price: item.price != null ? String(item.price) : '',
                    cost: item.cost != null ? String(item.cost) : '',
                    remarks: item.remarks || ''
                }))
                : [];

            setFormData({
                customerID: order.customerID || '',
                name: order.name || '',
                address: order.address || '',
                phoneNumber: order.phoneNumber || '',
                whatsappNumber: order.whatsappNumber || '',
                telegram: {
                    tgNumber: order.telegram?.tgNumber || '',
                    tgID: order.telegram?.tgID || '',
                    tgName: order.telegram?.tgName || ''
                },
                orderDate: order.orderDate ? new Date(order.orderDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                deliveryDate: order.deliveryDate ? new Date(order.deliveryDate).toISOString().split('T')[0] : '',
                orderStatus: order.orderStatus || 'delivered',
                supplier: order.supplier || 'Bean',
                supplierSource: order.supplierSource || 'BeanSup1',
                products: normalizedProducts,
                bundles: normalizedBundles,
                orderDeliveryFees: order.orderDeliveryFees != null ? String(order.orderDeliveryFees) : '0',
                supplierDeliveryFees: order.supplierDeliveryFees != null ? String(order.supplierDeliveryFees) : '0',
                remarks: order.remarks || '',
                creditDebits: (order.creditDebits || []).length
                    ? order.creditDebits.map((row) => ({
                        type: row.type === 'debit' ? 'debit' : 'credit',
                        amount: row.amount != null ? String(row.amount) : '',
                        remarks: row.remarks || ''
                    }))
                    : []
            });

            setProductSearchQueries(
                normalizedProducts.map((item) => {
                    const p = products.find((v) => v._id === item.product);
                    return p ? getProductLabel(p) : '';
                })
            );
            setBundleSearchQueries(
                normalizedBundles.map((item) => {
                    const b = bundles.find((v) => v._id === item.bundleProduct);
                    return b ? getBundleLabel(b) : '';
                })
            );
        }
    }, [mode, order, products, bundles]);

    const handleCustomerSelect = (customer) => {
        if (!customer) return;
        setSelectedCustomer(customer);
        setCustomerSearchQuery(`${customer.customerID} - ${(customer.names || []).join(', ')}`);
        setActiveCustomerSearch(false);
        setCustomerContactOptions({
            names: customer.names || [],
            addresses: customer.addresses || [],
            phoneNumbers: customer.phoneNumbers || [],
            whatsappNumbers: customer.whatsappNumbers || [],
            telegrams: customer.telegrams || []
        });

        setFormData((prev) => ({
            ...prev,
            customerID: customer.customerID || '',
            name: customer.names?.[0] || '',
            address: customer.addresses?.[0] || '',
            phoneNumber: customer.phoneNumbers?.[0] || '',
            whatsappNumber: customer.whatsappNumbers?.[0] || '',
            telegram: customer.telegrams?.[0]
                ? {
                    tgNumber: customer.telegrams[0].tgNumber || '',
                    tgID: customer.telegrams[0].tgID || '',
                    tgName: customer.telegrams[0].tgName || ''
                }
                : { ...emptyTelegram }
        }));
    };

    const filteredCustomers = (() => {
        const query = customerSearchQuery.trim();
        if (query.length < 3) return [];
        const normalizedQuery = normalizeSearchText(query);
        const localMatches = localCustomers.filter((customer) => {
            const customerNames = getCustomerNames(customer);
            const label = `${customer.customerID || ''} ${customerNames.join(' ')}`.trim();
            const searchable = [
                label,
                ...(customer.phoneNumbers || []),
                ...(customer.whatsappNumbers || []),
                ...(customer.addresses || [])
            ].join(' ');
            const normalized = normalizeSearchText(searchable);
            return normalized.includes(normalizedQuery);
        });
        const merged = [...customerSearchResults, ...localMatches];
        const seen = new Set();
        return merged.filter((customer) => {
            const key = String(customer?._id || customer?.customerID || '');
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    })();

    const truncateMoney2 = (n) => {
        if (n == null || !Number.isFinite(Number(n))) return 0;
        const num = Number(n);
        const eps = num >= 0 ? 1e-9 : -1e-9;
        return Math.trunc((num + eps) * 100) / 100;
    };

    // Accepts simple expressions like `21*3` and returns a number truncated to 2 decimals.
    // Security: only evaluates when the input matches an allowlist regex.
    const evaluateMoneyExpression = (raw) => {
        const expr = String(raw ?? '').trim();
        if (!expr) return null;
        const sanitized = expr.replace(/\s+/g, '');
        if (!/^[0-9()+\-*/.]+$/.test(sanitized)) return null;

        try {
            // eslint-disable-next-line no-new-func
            const result = Function(`"use strict"; return (${sanitized});`)();
            if (!Number.isFinite(result)) return null;
            return truncateMoney2(result);
        } catch {
            return null;
        }
    };

    const parseMoneyInputToNumber = (raw) => {
        if (raw == null) return undefined;
        const exprEval = evaluateMoneyExpression(raw);
        if (exprEval != null) return exprEval;
        const n = parseFloat(String(raw));
        if (!Number.isFinite(n)) return undefined;
        return truncateMoney2(n);
    };

    const getRemarksRows = (remarks) => {
        const text = String(remarks ?? '');
        const lineCount = text ? text.split(/\r?\n/).length : 1;
        return Math.max(2, lineCount);
    };

    const updateProductLine = (index, field, value) => {
        const next = [...formData.products];
        next[index] = { ...(next[index] || emptyProductLine), [field]: value };
        const selectedProduct = products.find((item) => item._id === next[index].product);
        // Keep price/cost editable manually from frontend:
        // auto-calc only when selecting product or changing quantity.
        if (selectedProduct && (field === 'product' || field === 'quantity')) {
            const quantity = Number.parseInt(next[index].quantity || '0', 10) || 0;
            const resolvedUp = getResolvedProductUnitPrice(selectedProduct, formData.orderDate);
            const unitPrice = Number(resolvedUp != null ? resolvedUp : 0);
            const unitCost = getResolvedProductUnitCost(selectedProduct, formData.supplier, formData.supplierSource, formData.orderDate);
            next[index].price = String(truncateMoney2(unitPrice * quantity));
            next[index].cost = String(truncateMoney2(unitCost * quantity));
        }
        setFormData((prev) => ({ ...prev, products: next }));
    };

    const updateBundleLine = (index, field, value) => {
        const next = [...formData.bundles];
        next[index] = { ...(next[index] || emptyBundleLine), [field]: value };
        const selectedBundle = bundles.find((item) => item._id === next[index].bundleProduct);
        // Keep price/cost editable manually from frontend:
        // auto-calc only when selecting bundle or changing quantity.
        if (selectedBundle && (field === 'bundleProduct' || field === 'quantity')) {
            const quantity = Number.parseInt(next[index].quantity || '0', 10) || 0;
            const unitPrice = Number(getLatestBundlePrice(selectedBundle) || 0);
            const unitCost = getResolvedBundleUnitCost(selectedBundle, products, formData.supplier, formData.supplierSource, formData.orderDate);
            next[index].price = String(truncateMoney2(unitPrice * quantity));
            next[index].cost = String(truncateMoney2(unitCost * quantity));
        }
        setFormData((prev) => ({ ...prev, bundles: next }));
    };

    const updateProductSearchQuery = (index, value) => {
        const next = [...productSearchQueries];
        next[index] = value;
        setProductSearchQueries(next);
    };

    const updateBundleSearchQuery = (index, value) => {
        const next = [...bundleSearchQueries];
        next[index] = value;
        setBundleSearchQueries(next);
    };

    const getFilteredProducts = (query) => {
        const trimmed = String(query || '').trim().toLowerCase();
        if (trimmed.length <= 3) return [];
        const normalizedQuery = normalizeSearchText(trimmed);
        return products.filter((product) => {
            const label = getProductLabel(product).toLowerCase();
            return normalizeSearchText(label).includes(normalizedQuery);
        });
    };

    const getFilteredBundles = (query) => {
        const trimmed = String(query || '').trim().toLowerCase();
        if (trimmed.length <= 3) return [];
        const normalizedQuery = normalizeSearchText(trimmed);
        return bundles.filter((bundle) => normalizeSearchText(getBundleLabel(bundle)).includes(normalizedQuery));
    };

    const selectProductFromSearch = (index, productId) => {
        const selectedProduct = products.find((item) => item._id === productId);
        updateProductLine(index, 'product', productId);
        updateProductSearchQuery(index, selectedProduct ? getProductLabel(selectedProduct) : '');
        setActiveProductSearchIndex(-1);
    };

    const selectBundleFromSearch = (index, bundleId) => {
        const selectedBundle = bundles.find((item) => item._id === bundleId);
        updateBundleLine(index, 'bundleProduct', bundleId);
        updateBundleSearchQuery(index, selectedBundle ? getBundleLabel(selectedBundle) : '');
        setActiveBundleSearchIndex(-1);
    };

    const addProductLine = () => {
        setFormData((prev) => ({ ...prev, products: [...prev.products, { ...emptyProductLine }] }));
        setProductSearchQueries((prev) => [...prev, '']);
    };
    const addBundleLine = () => {
        setFormData((prev) => ({ ...prev, bundles: [...prev.bundles, { ...emptyBundleLine }] }));
        setBundleSearchQueries((prev) => [...prev, '']);
    };

    const removeProductLine = (index) => {
        const next = formData.products.filter((_, itemIndex) => itemIndex !== index);
        setFormData((prev) => ({ ...prev, products: next.length ? next : [{ ...emptyProductLine }] }));
        const nextQueries = productSearchQueries.filter((_, itemIndex) => itemIndex !== index);
        setProductSearchQueries(nextQueries.length ? nextQueries : ['']);
    };

    const removeBundleLine = (index) => {
        const next = formData.bundles.filter((_, itemIndex) => itemIndex !== index);
        setFormData((prev) => ({ ...prev, bundles: next }));
        const nextQueries = bundleSearchQueries.filter((_, itemIndex) => itemIndex !== index);
        setBundleSearchQueries(nextQueries);
    };

    const addCreditDebitLine = () => {
        setFormData((prev) => ({
            ...prev,
            creditDebits: [...(prev.creditDebits || []), { ...emptyCreditDebitLine }]
        }));
    };

    const removeCreditDebitLine = (index) => {
        setFormData((prev) => ({
            ...prev,
            creditDebits: (prev.creditDebits || []).filter((_, i) => i !== index)
        }));
    };

    const updateCreditDebitLine = (index, field, value) => {
        setFormData((prev) => {
            const next = [...(prev.creditDebits || [])];
            next[index] = { ...(next[index] || { ...emptyCreditDebitLine }), [field]: value };
            return { ...prev, creditDebits: next };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                customerID: formData.customerID.trim(),
                name: formData.name.trim(),
                address: formData.address.trim(),
                phoneNumber: formData.phoneNumber.trim(),
                whatsappNumber: formData.whatsappNumber.trim(),
                telegram: {
                    tgNumber: formData.telegram.tgNumber.trim() || undefined,
                    tgID: formData.telegram.tgID.trim() || undefined,
                    tgName: formData.telegram.tgName.trim() || undefined
                },
                orderDate: formData.orderDate,
                deliveryDate: formData.deliveryDate || undefined,
                orderStatus: formData.orderStatus,
                supplier: formData.supplier,
                supplierSource: formData.supplierSource,
                remarks: formData.remarks.trim(),
                products: formData.products
                    .map((item) => ({
                        product: item.product,
                        quantity: parseInt(item.quantity || '0', 10),
                        price: item.price !== '' ? parseMoneyInputToNumber(item.price) : undefined,
                        cost: item.cost !== '' ? parseMoneyInputToNumber(item.cost) : undefined,
                        remarks: item.remarks != null && String(item.remarks).trim().length > 0 ? item.remarks : undefined
                    }))
                    .filter((item) => item.product && item.quantity > 0),
                bundles: formData.bundles
                    .map((item) => ({
                        bundleProduct: item.bundleProduct,
                        quantity: parseInt(item.quantity || '0', 10),
                        price: item.price !== '' ? parseMoneyInputToNumber(item.price) : undefined,
                        cost: item.cost !== '' ? parseMoneyInputToNumber(item.cost) : undefined,
                        remarks: item.remarks != null && String(item.remarks).trim().length > 0 ? item.remarks : undefined
                    }))
                    .filter((item) => item.bundleProduct && item.quantity > 0),
                orderDeliveryFees: parseFloat(formData.orderDeliveryFees || '0'),
                supplierDeliveryFees: parseFloat(formData.supplierDeliveryFees || '0'),
                creditDebits: (formData.creditDebits || [])
                    .map((row) => ({
                        type: row.type === 'debit' ? 'debit' : 'credit',
                        amount:
                            row.amount !== '' && row.amount != null ? parseMoneyInputToNumber(row.amount) : 0,
                        remarks:
                            row.remarks != null && String(row.remarks).trim().length > 0
                                ? row.remarks
                                : undefined
                    }))
                    .filter((row) => row.amount != null && Number(row.amount) > 0)
            };

            const token = localStorage.getItem('token');
            const request = mode === 'edit'
                ? axios.put(`${process.env.REACT_APP_API_URL}/api/simonorders/${order._id}`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                })
                : axios.post(`${process.env.REACT_APP_API_URL}/api/simonorders`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                });

            const response = await request;
            if (response.data.success) {
                onSuccess(response.data.data);
            }
        } catch (err) {
            setError(`Failed to ${mode} Simon order: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const toNumberOr0 = (value) => {
        const n = parseFloat(value);
        return Number.isFinite(n) ? n : 0;
    };

    // Match backend behavior: truncate (no rounding) to 2 decimals.
    const formatMoney = (value) => `$${truncateMoney2(toNumberOr0(value)).toFixed(2)}`;

    const selectedProductLines = (formData.products || []).filter((l) => l?.product && Number.parseInt(String(l.quantity || '0'), 10) > 0);
    const selectedBundleLines = (formData.bundles || []).filter((l) => l?.bundleProduct && Number.parseInt(String(l.quantity || '0'), 10) > 0);

    const productLinePrices = selectedProductLines.map((l) => truncateMoney2(toNumberOr0(l.price)));
    const bundleLinePrices = selectedBundleLines.map((l) => truncateMoney2(toNumberOr0(l.price)));

    const productLineCosts = selectedProductLines.map((l) => truncateMoney2(toNumberOr0(l.cost)));
    const bundleLineCosts = selectedBundleLines.map((l) => truncateMoney2(toNumberOr0(l.cost)));

    const productPriceTotal = productLinePrices.reduce((sum, v) => sum + v, 0);
    const bundlePriceTotal = bundleLinePrices.reduce((sum, v) => sum + v, 0);

    const orderDeliveryFeesValue = truncateMoney2(toNumberOr0(formData.orderDeliveryFees));
    const supplierDeliveryFeesValue = truncateMoney2(toNumberOr0(formData.supplierDeliveryFees));

    const totalSellingPrice = truncateMoney2(productPriceTotal + bundlePriceTotal + orderDeliveryFeesValue);

    const costTotalBeforeSupplier = productLineCosts.reduce((sum, v) => sum + v, 0) + bundleLineCosts.reduce((sum, v) => sum + v, 0);
    const totalCost = truncateMoney2(costTotalBeforeSupplier + supplierDeliveryFeesValue);

    const baseCommissionValue = truncateMoney2(totalSellingPrice - totalCost);
    let creditDebitAdjust = 0;
    for (const row of formData.creditDebits || []) {
        const raw = row?.amount;
        const amt =
            raw !== '' && raw != null ? parseMoneyInputToNumber(raw) : null;
        if (amt == null || !Number.isFinite(amt) || amt <= 0) continue;
        creditDebitAdjust += row.type === 'debit' ? -amt : amt;
    }
    const commissionValue = truncateMoney2(baseCommissionValue + creditDebitAdjust);

    const joinMoneyParts = (values) => {
        if (!values.length) return '0.00';
        return values.map((v) => truncateMoney2(toNumberOr0(v)).toFixed(2)).join(' + ');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl my-8 min-w-0 overflow-x-hidden">
                <div className="flex justify-between items-center mb-4 gap-4">
                    <h2 className="text-xl font-bold whitespace-nowrap">{mode === 'edit' ? 'Edit Simon Order' : 'Create Simon Order'}</h2>
                    <div className="flex items-center justify-center gap-4 flex-wrap text-sm">
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-blue-700 font-semibold">Total Selling Price :</span>
                            <span className="text-green-700 font-semibold">{formatMoney(totalSellingPrice)}</span>
                        </div>
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-blue-700 font-semibold">Total Cost :</span>
                            <span className="text-green-700 font-semibold">{formatMoney(totalCost)}</span>
                        </div>
                        <div className="flex items-center gap-1 whitespace-nowrap">
                            <span className="text-blue-700 font-semibold">Total Comms :</span>
                            <span className="text-green-700 font-semibold">{formatMoney(commissionValue)}</span>
                        </div>
                    </div>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700" aria-label="Close modal">
                        <FiX className="w-6 h-6" />
                    </button>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium">Customer</h3>
                            </div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        required
                                        value={customerSearchQuery}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            setCustomerSearchQuery(next);
                                            setActiveCustomerSearch(true);
                                            if (!next.trim()) {
                                                setSelectedCustomer(null);
                                                setCustomerContactOptions({
                                                    names: [],
                                                    addresses: [],
                                                    phoneNumbers: [],
                                                    whatsappNumbers: [],
                                                    telegrams: []
                                                });
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    customerID: '',
                                                    name: '',
                                                    address: '',
                                                    phoneNumber: '',
                                                    whatsappNumber: '',
                                                    telegram: { ...emptyTelegram }
                                                }));
                                            }
                                        }}
                                        onFocus={() => setActiveCustomerSearch(true)}
                                        className={inputClassName}
                                        placeholder="Type to search by Customer ID or Name..."
                                    />
                                    {activeCustomerSearch && customerSearchQuery.trim().length >= 3 && (
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow max-h-56 overflow-y-auto">
                                            {customerSearchLoading ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">Searching...</div>
                                            ) : filteredCustomers.length === 0 ? (
                                                <div className="px-3 py-2 text-sm text-gray-500">No customers found</div>
                                            ) : (
                                                filteredCustomers.map((customer) => (
                                                    <button
                                                        key={customer._id}
                                                        type="button"
                                                        onClick={() => handleCustomerSelect(customer)}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 font-bold"
                                                    >
                                                        {customer.customerID} - {getCustomerNames(customer).join(', ') || 'No Name'}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowCreateCustomerModal(true)}
                                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 whitespace-nowrap"
                                >
                                    Create New Customer
                                </button>
                            </div>

                            {selectedCustomer && (
                                <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowEditCustomerModal(true)}
                                        className="absolute top-2 right-2 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        Edit Customer
                                    </button>
                                    <div className="pr-28">
                                        <div className="font-semibold text-gray-900 mb-3">
                                            {selectedCustomer.customerID} - {(selectedCustomer.names || []).join(', ') || 'No Name'}
                                        </div>
                                        <div className="text-sm font-medium text-gray-700 mb-2">Contact for this order (choose one per type)</div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
                                            <div>
                                                <label className="block text-gray-600 mb-0.5">Name ({(customerContactOptions.names || []).length})</label>
                                                <select value={formData.name} onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value, customerID: selectedCustomer.customerID || prev.customerID }))} className={inputClassName}>
                                                    <option value="">—</option>
                                                    {(customerContactOptions.names || []).map((nameOption, idx) => (
                                                        <option key={`name-option-${idx}`} value={nameOption}>{nameOption}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="min-w-0">
                                                <label className="block text-gray-600 mb-0.5">Address ({(customerContactOptions.addresses || []).length})</label>
                                                <select value={formData.address} onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))} className={inputClassName}>
                                                    <option value="">—</option>
                                                    {(customerContactOptions.addresses || []).map((addressOption, idx) => (
                                                        <option key={`address-option-${idx}`} value={addressOption}>{addressOption}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-gray-600 mb-0.5">Phone ({(customerContactOptions.phoneNumbers || []).length})</label>
                                                <select value={formData.phoneNumber} onChange={(e) => setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))} className={inputClassName}>
                                                    <option value="">—</option>
                                                    {(customerContactOptions.phoneNumbers || []).map((phoneOption, idx) => (
                                                        <option key={`phone-option-${idx}`} value={phoneOption}>{phoneOption}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-gray-600 mb-0.5">WhatsApp ({(customerContactOptions.whatsappNumbers || []).length})</label>
                                                <select value={formData.whatsappNumber} onChange={(e) => setFormData((prev) => ({ ...prev, whatsappNumber: e.target.value }))} className={inputClassName}>
                                                    <option value="">—</option>
                                                    {(customerContactOptions.whatsappNumbers || []).map((whatsappOption, idx) => (
                                                        <option key={`whatsapp-option-${idx}`} value={whatsappOption}>{whatsappOption}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-gray-600 mb-0.5">Telegram ({(customerContactOptions.telegrams || []).length})</label>
                                                <select
                                                    value={`${formData.telegram.tgName || ''}|${formData.telegram.tgNumber || ''}|${formData.telegram.tgID || ''}`}
                                                    onChange={(e) => {
                                                        const [tgName, tgNumber, tgID] = e.target.value.split('|');
                                                        setFormData((prev) => ({ ...prev, telegram: { tgName: tgName || '', tgNumber: tgNumber || '', tgID: tgID || '' } }));
                                                    }}
                                                    className={inputClassName}
                                                >
                                                    <option value="||">—</option>
                                                    {(customerContactOptions.telegrams || []).map((telegramOption, idx) => (
                                                        <option
                                                            key={`telegram-option-${idx}`}
                                                            value={`${telegramOption.tgName || ''}|${telegramOption.tgNumber || ''}|${telegramOption.tgID || ''}`}
                                                        >
                                                            {telegramOption.tgName || telegramOption.tgNumber || telegramOption.tgID || 'Telegram'}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium">Order Details</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.orderDate}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            orderDate: e.target.value,
                                            deliveryDate: e.target.value
                                        }))}
                                        className={inputClassName}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Date</label>
                                    <input type="date" value={formData.deliveryDate} onChange={(e) => setFormData((prev) => ({ ...prev, deliveryDate: e.target.value }))} className={inputClassName} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Status *</label>
                                    <select value={formData.orderStatus} onChange={(e) => setFormData((prev) => ({ ...prev, orderStatus: e.target.value }))} className={inputClassName}>
                                        <option value="delivered">Delivered</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="credit">Credit</option>
                                        <option value="debit">Debit</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                                    <select
                                        value={formData.supplier}
                                        onChange={(e) => {
                                            const nextSource = SOURCE_CODES_BY_SUPPLIER[e.target.value]?.[0] || '';
                                            setFormData((prev) => ({
                                                ...prev,
                                                supplier: e.target.value,
                                                supplierSource: nextSource,
                                                supplierDeliveryFees: getSupplierDeliveryFeeBySource(nextSource) || prev.supplierDeliveryFees
                                            }));
                                        }}
                                        className={inputClassName}
                                    >
                                        <option value="Bean">Bean</option>
                                        <option value="Doraemon">Doraemon</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Source *</label>
                                    <select
                                        value={formData.supplierSource}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            supplierSource: e.target.value,
                                            supplierDeliveryFees: getSupplierDeliveryFeeBySource(e.target.value) || prev.supplierDeliveryFees
                                        }))}
                                        className={inputClassName}
                                    >
                                        {(SOURCE_CODES_BY_SUPPLIER[formData.supplier] || []).map((sourceCode) => (
                                            <option key={sourceCode} value={sourceCode}>{sourceCode}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Order Delivery Fees</label>
                                    <input type="number" step="0.01" value={formData.orderDeliveryFees} onChange={(e) => setFormData((prev) => ({ ...prev, orderDeliveryFees: e.target.value }))} className={inputClassName} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Supplier Delivery Fees</label>
                                    <input type="number" step="0.01" value={formData.supplierDeliveryFees} onChange={(e) => setFormData((prev) => ({ ...prev, supplierDeliveryFees: e.target.value }))} className={inputClassName} />
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Order Remarks</label>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
                                    className={inputClassName}
                                    rows={2}
                                    placeholder="Optional remarks for this order"
                                />
                            </div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium">Products</h3>
                                <button type="button" onClick={addProductLine} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">+ Add Product</button>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">Type to search product (min 4 chars). Leave cost blank to auto-resolve by supplier source and order date.</p>
                            <div className="space-y-3">
                                {formData.products.map((item, index) => (
                                    <div key={`order-product-${index}`} className="border border-gray-200 rounded-md p-3 bg-white">
                                        <div className="relative mb-2">
                                            <input
                                                type="text"
                                                value={productSearchQueries[index] || ''}
                                                onChange={(e) => updateProductSearchQuery(index, e.target.value)}
                                                onFocus={() => setActiveProductSearchIndex(index)}
                                                className={inputClassName}
                                                placeholder="Type product (min 4 chars)"
                                            />
                                            {activeProductSearchIndex === index && String(productSearchQueries[index] || '').trim().length > 3 && (
                                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow max-h-56 overflow-y-auto">
                                                    {getFilteredProducts(productSearchQueries[index]).length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No products found</div>
                                                    ) : (
                                                        getFilteredProducts(productSearchQueries[index]).map((product) => (
                                                            <button
                                                                key={product._id}
                                                                type="button"
                                                                onClick={() => selectProductFromSearch(index, product._id)}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 font-bold"
                                                            >
                                                                {getProductLabel(product)}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                                        <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Quantity :</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={(e) => updateProductLine(index, 'quantity', e.target.value)}
                                                    className={labeledInputFieldClassName}
                                                />
                                            </div>
                                            <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Price :</span>
                                                <input
                                                type="text"
                                                inputMode="decimal"
                                                    value={item.price}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value;
                                                    const trimmed = rawValue.trim();
                                                    const isIncomplete = /[+\-*/.]$/.test(trimmed);
                                                    const evaluated = trimmed && !isIncomplete ? evaluateMoneyExpression(trimmed) : null;
                                                    updateProductLine(index, 'price', evaluated != null ? String(evaluated) : rawValue);
                                                }}
                                                    className={labeledInputFieldClassName}
                                                placeholder="e.g. 21*3"
                                                />
                                            </div>
                                            <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Cost :</span>
                                                <input
                                                type="text"
                                                inputMode="decimal"
                                                    value={item.cost}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value;
                                                    const trimmed = rawValue.trim();
                                                    const isIncomplete = /[+\-*/.]$/.test(trimmed);
                                                    const evaluated = trimmed && !isIncomplete ? evaluateMoneyExpression(trimmed) : null;
                                                    updateProductLine(index, 'cost', evaluated != null ? String(evaluated) : rawValue);
                                                }}
                                                    className={labeledInputFieldClassName}
                                                placeholder="e.g. 20*3"
                                                />
                                            </div>
                                            <div className={`${labeledTextAreaWrapperClassName} md:col-span-2`}>
                                                <span className={labeledInputLabelClassName}>Remarks :</span>
                                                <textarea
                                                    rows={getRemarksRows(item.remarks)}
                                                    value={item.remarks || ''}
                                                    onChange={(e) => updateProductLine(index, 'remarks', e.target.value)}
                                                    className={labeledTextAreaFieldClassName}
                                                    placeholder="(optional)"
                                                />
                                            </div>
                                            <div className="flex justify-end md:col-span-1">
                                                {formData.products.length > 1 && (
                                                    <button type="button" onClick={() => removeProductLine(index)} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Remove</button>
                                                )}
                                            </div>
                                        </div>

                                        {(() => {
                                            const selectedProduct = products.find((p) => String(p._id) === String(item.product));
                                            if (!selectedProduct) return null;
                                            const snapshot = getProductLatestPricingSnapshot(selectedProduct, formData.orderDate);
                                            return (
                                                <div className="mt-2 overflow-x-auto">
                                                    <table className="min-w-max w-full table-auto text-[11px] border border-gray-200 rounded-sm">
                                                        <thead className="bg-gray-50">
                                                            <tr>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">Date</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">Unit Price</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">DoraemonSupB</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">DoraemonSupCD</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup1</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup2</th>
                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup3</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="bg-white">
                                                            <tr>
                                                                <td className="px-2 py-1 whitespace-nowrap">{snapshot.latestEffectiveDateStr}</td>
                                                                <td className="px-2 py-1 whitespace-nowrap">{snapshot.unitPriceValue != null ? formatMoneyOrNA(snapshot.unitPriceValue) : 'N/A'}</td>
                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                    {snapshot.costsBySourceCode.DoraemonSupB?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.DoraemonSupB.cost) : 'N/A'}
                                                                </td>
                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                    {snapshot.costsBySourceCode.DoraemonSupCD?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.DoraemonSupCD.cost) : 'N/A'}
                                                                </td>
                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                    {snapshot.costsBySourceCode.BeanSup1?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup1.cost) : 'N/A'}
                                                                </td>
                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                    {snapshot.costsBySourceCode.BeanSup2?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup2.cost) : 'N/A'}
                                                                </td>
                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                    {snapshot.costsBySourceCode.BeanSup3?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup3.cost) : 'N/A'}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium">Bundles</h3>
                                <button type="button" onClick={addBundleLine} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">+ Add Bundle</button>
                            </div>
                            <p className="text-xs text-gray-500 mb-3">Type to search bundle (min 4 chars). Leave cost blank to auto-resolve from linked products.</p>
                            <div className="space-y-3">
                                {formData.bundles.map((item, index) => (
                                    <div key={`order-bundle-${index}`} className="border border-gray-200 rounded-md p-3 bg-white">
                                        <div className="relative mb-2">
                                            <input
                                                type="text"
                                                value={bundleSearchQueries[index] || ''}
                                                onChange={(e) => updateBundleSearchQuery(index, e.target.value)}
                                                onFocus={() => setActiveBundleSearchIndex(index)}
                                                className={inputClassName}
                                                placeholder="Type bundle (min 4 chars)"
                                            />
                                            {activeBundleSearchIndex === index && String(bundleSearchQueries[index] || '').trim().length > 3 && (
                                                <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow max-h-56 overflow-y-auto">
                                                    {getFilteredBundles(bundleSearchQueries[index]).length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">No bundles found</div>
                                                    ) : (
                                                        getFilteredBundles(bundleSearchQueries[index]).map((bundleItem) => (
                                                            <button
                                                                key={bundleItem._id}
                                                                type="button"
                                                                onClick={() => selectBundleFromSearch(index, bundleItem._id)}
                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 text-blue-700 font-bold"
                                                            >
                                                                {getBundleLabel(bundleItem)}
                                                            </button>
                                                        ))
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                                            <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Quantity :</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.quantity}
                                                    onChange={(e) => updateBundleLine(index, 'quantity', e.target.value)}
                                                    className={labeledInputFieldClassName}
                                                />
                                            </div>
                                            <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Price :</span>
                                                <input
                                                type="text"
                                                inputMode="decimal"
                                                    value={item.price}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value;
                                                    const trimmed = rawValue.trim();
                                                    const isIncomplete = /[+\-*/.]$/.test(trimmed);
                                                    const evaluated = trimmed && !isIncomplete ? evaluateMoneyExpression(trimmed) : null;
                                                    updateBundleLine(index, 'price', evaluated != null ? String(evaluated) : rawValue);
                                                }}
                                                    className={labeledInputFieldClassName}
                                                placeholder="e.g. 140/2"
                                                />
                                            </div>
                                            <div className={labeledInputWrapperClassName}>
                                                <span className={labeledInputLabelClassName}>Cost :</span>
                                                <input
                                                type="text"
                                                inputMode="decimal"
                                                    value={item.cost}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value;
                                                    const trimmed = rawValue.trim();
                                                    const isIncomplete = /[+\-*/.]$/.test(trimmed);
                                                    const evaluated = trimmed && !isIncomplete ? evaluateMoneyExpression(trimmed) : null;
                                                    updateBundleLine(index, 'cost', evaluated != null ? String(evaluated) : rawValue);
                                                }}
                                                    className={labeledInputFieldClassName}
                                                placeholder="e.g. 80*2"
                                                />
                                            </div>
                                            <div className={`${labeledTextAreaWrapperClassName} md:col-span-2`}>
                                                <span className={labeledInputLabelClassName}>Remarks :</span>
                                                <textarea
                                                    rows={getRemarksRows(item.remarks)}
                                                    value={item.remarks || ''}
                                                    onChange={(e) => updateBundleLine(index, 'remarks', e.target.value)}
                                                    className={labeledTextAreaFieldClassName}
                                                    placeholder="(optional)"
                                                />
                                            </div>
                                            <div className="flex justify-end md:col-span-1">
                                                <button type="button" onClick={() => removeBundleLine(index)} className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">Remove</button>
                                            </div>
                                        </div>

                                        {(() => {
                                            const selectedBundle = bundles.find((b) => String(b._id) === String(item.bundleProduct));
                                            if (!selectedBundle) return null;
                                            const bundleProducts = Array.isArray(selectedBundle.products) ? selectedBundle.products : [];
                                            if (!bundleProducts.length) return null;

                                            return (
                                                <div className="mt-2 space-y-2">
                                                    {bundleProducts.map((bundleItem, i) => {
                                                        const productId = bundleItem?.product?._id || bundleItem?.product;
                                                        const productDoc = products.find((p) => String(p._id) === String(productId));
                                                        if (!productDoc) return null;
                                                        const snapshot = getProductLatestPricingSnapshot(productDoc, formData.orderDate);

                                                        return (
                                                            <div key={`${item.bundleProduct}-${i}`} className="border border-gray-200 rounded-sm bg-white">
                                                                <div className="px-2 py-1 text-[11px] font-semibold text-gray-800 whitespace-nowrap">
                                                                    {getProductLabel(productDoc)} {bundleItem?.quantity ? `(x${bundleItem.quantity})` : ''}
                                                                </div>
                                                                <div className="overflow-x-auto px-1 pb-1">
                                                                    <table className="min-w-max w-full table-auto text-[11px] border border-gray-200 rounded-sm">
                                                                        <thead className="bg-gray-50">
                                                                            <tr>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">Date</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">Unit Price</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">DoraemonSupB</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">DoraemonSupCD</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup1</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup2</th>
                                                                                <th className="px-2 py-1 text-left whitespace-nowrap">BeanSup3</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white">
                                                                            <tr>
                                                                                <td className="px-2 py-1 whitespace-nowrap">{snapshot.latestEffectiveDateStr}</td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">{snapshot.unitPriceValue != null ? formatMoneyOrNA(snapshot.unitPriceValue) : 'N/A'}</td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                                    {snapshot.costsBySourceCode.DoraemonSupB?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.DoraemonSupB.cost) : 'N/A'}
                                                                                </td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                                    {snapshot.costsBySourceCode.DoraemonSupCD?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.DoraemonSupCD.cost) : 'N/A'}
                                                                                </td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                                    {snapshot.costsBySourceCode.BeanSup1?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup1.cost) : 'N/A'}
                                                                                </td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                                    {snapshot.costsBySourceCode.BeanSup2?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup2.cost) : 'N/A'}
                                                                                </td>
                                                                                <td className="px-2 py-1 whitespace-nowrap">
                                                                                    {snapshot.costsBySourceCode.BeanSup3?.cost != null ? formatMoneyOrNA(snapshot.costsBySourceCode.BeanSup3.cost) : 'N/A'}
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-rose-50 p-4 rounded-lg border border-rose-100">
                            <div className="flex justify-between items-center mb-3">
                                <h3 className="text-lg font-medium">Credit / debit (misc)</h3>
                                <button type="button" onClick={addCreditDebitLine} className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                                    + Add line
                                </button>
                            </div>
                            <p className="text-xs text-gray-600 mb-3">
                                Credit increases total commission; debit decreases it. Amounts are ≥ 0; type chooses the sign applied to commission.
                            </p>
                            {(formData.creditDebits || []).length === 0 ? (
                                <div className="text-sm text-gray-500">No credit/debit lines. Use &quot;+ Add line&quot; if needed.</div>
                            ) : (
                                <div className="space-y-2">
                                    {(formData.creditDebits || []).map((row, index) => (
                                        <div key={`credit-debit-${index}`} className="flex flex-wrap items-end gap-2 border border-rose-100 rounded-md p-2 bg-white">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Type</label>
                                                <select
                                                    value={row.type || 'credit'}
                                                    onChange={(e) => updateCreditDebitLine(index, 'type', e.target.value)}
                                                    className={inputClassName}
                                                >
                                                    <option value="credit">Credit (+comms)</option>
                                                    <option value="debit">Debit (−comms)</option>
                                                </select>
                                            </div>
                                            <div className="flex-1 min-w-[120px]">
                                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Amount</label>
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={row.amount ?? ''}
                                                    onChange={(e) => {
                                                        const rawValue = e.target.value;
                                                        const trimmed = rawValue.trim();
                                                        const isIncomplete = /[+\-*/.]$/.test(trimmed);
                                                        const evaluated = trimmed && !isIncomplete ? evaluateMoneyExpression(trimmed) : null;
                                                        updateCreditDebitLine(index, 'amount', evaluated != null ? String(evaluated) : rawValue);
                                                    }}
                                                    className={inputClassName}
                                                    placeholder="e.g. 10 or 5*2"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-[160px]">
                                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Remarks</label>
                                                <input
                                                    type="text"
                                                    value={row.remarks || ''}
                                                    onChange={(e) => updateCreditDebitLine(index, 'remarks', e.target.value)}
                                                    className={inputClassName}
                                                    placeholder="(optional)"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeCreditDebitLine(index)}
                                                className="px-3 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">Commission Summary</h3>
                            <div className="text-sm text-gray-800 space-y-1">
                                <div>
                                    <span className="font-semibold">Product Price :</span>{' '}
                                    {joinMoneyParts(productLinePrices)} = {formatMoney(productPriceTotal)}
                                </div>
                                <div>
                                    <span className="font-semibold">Bundle Price :</span>{' '}
                                    {joinMoneyParts(bundleLinePrices)} = {formatMoney(bundlePriceTotal)}
                                </div>
                                <div>
                                    <span className="font-semibold">Delivery Fees :</span>{' '}
                                    {formatMoney(orderDeliveryFeesValue)}
                                </div>
                                <div>
                                    <span className="font-semibold">Total Selling Price :</span>{' '}
                                    {formatMoney(totalSellingPrice)}
                                </div>

                                <div className="pt-2">
                                    <span className="font-semibold">Cost :</span>{' '}
                                    {joinMoneyParts([...productLineCosts, ...bundleLineCosts])}{' '}
                                    = {formatMoney(costTotalBeforeSupplier)}
                                </div>
                                <div>
                                    <span className="font-semibold">Supplier Delivery Fees :</span>{' '}
                                    {formatMoney(supplierDeliveryFeesValue)}
                                </div>
                                <div>
                                    <span className="font-semibold">Total Cost :</span>{' '}
                                    {formatMoney(totalCost)}
                                </div>

                                <div className="pt-1">
                                    <span className="font-semibold">Base commission (lines &amp; fees) :</span>{' '}
                                    {formatMoney(baseCommissionValue)}
                                </div>
                                {creditDebitAdjust !== 0 && (
                                    <div>
                                        <span className="font-semibold">Misc credit/debit (net to commission) :</span>{' '}
                                        {formatMoney(creditDebitAdjust)}
                                    </div>
                                )}

                                <div className="pt-1">
                                    <span className="font-semibold">Commission (final) :</span>{' '}
                                    {formatMoney(commissionValue)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 mt-6">
                        <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400" disabled={loading}>Cancel</button>
                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" disabled={loading}>
                            {loading ? (mode === 'edit' ? 'Updating...' : 'Creating...') : (mode === 'edit' ? 'Update' : 'Create')}
                        </button>
                    </div>
                </form>
            </div>
            {showCreateCustomerModal && (
                <SimonCustomerModal
                    mode="create"
                    onSuccess={(newCustomer) => {
                        setLocalCustomers((prev) => [newCustomer, ...prev]);
                        setShowCreateCustomerModal(false);
                        handleCustomerSelect(newCustomer);
                    }}
                    onCancel={() => setShowCreateCustomerModal(false)}
                />
            )}
            {showEditCustomerModal && selectedCustomer && (
                <SimonCustomerModal
                    mode="edit"
                    customer={selectedCustomer}
                    onSuccess={(updatedCustomer) => {
                        setLocalCustomers((prev) =>
                            prev.map((item) => (item._id === updatedCustomer._id ? updatedCustomer : item))
                        );
                        setSelectedCustomer(updatedCustomer);
                        setShowEditCustomerModal(false);
                        handleCustomerSelect(updatedCustomer);
                    }}
                    onCancel={() => setShowEditCustomerModal(false)}
                />
            )}
        </div>
    );
};

export default SimonOrderModal;
